// Page registry for the admin portal.
//
// Each real page module side-effect-registers itself with the router via
// `registerPage(...)`. Pages not yet extracted are represented by stubs in
// this file. After all imports have settled, `setOrder` enforces the
// canonical sidebar order regardless of import-hoisting.
//
// Sibling agents are writing the page modules in parallel. When a real page
// is in place its `registerPage` call beats the stub fallback below (because
// the imports above run before this file's body executes).

import { registerPage, getPage, setOrder } from "./router";
import type { Page, PageContext, PageCleanup } from "./types";

// ── Real pages — side-effect imports register themselves ────────────────────
// These run before the rest of this file (ES module imports are hoisted).
// `setOrder` at the bottom of the file fixes the sidebar order regardless.

import "../pages/dashboard/page";
import "../pages/demo/page";
import "../pages/retailers/page";
import "../pages/prospects/page";
import "../pages/suppliers/page";
import "../pages/catalogue/page";
import "../pages/catalogue-pdf-import/page";
import "../pages/catalogue-supplier-import/page";
import "../pages/supplier-discounts/page";
import "../pages/door-ranges/page";
import "../pages/catalogue-access/page";
import "../pages/communication-monitor/page";
import "../pages/phone-numbers/page";
import "../pages/onboarding/page";
import "../pages/analytics/page";
import "../pages/usage-analytics/page";
import "../pages/email/page";
import "../pages/social/page";
import "../pages/tasks/page";
import "../pages/notes/page";
import "../pages/events/page";
import "../pages/links/page";
import "../pages/settings/page";
import "../pages/feedback/page";
import "../pages/roadmap/page";
import "../pages/system-health/page";
import "../pages/flagged-layouts/page";
import "../pages/app-launcher/page";
import "../pages/gdpr/page";

// ── Stub specs ──────────────────────────────────────────────────────────────
// Stubs are only registered if a real page hasn't been registered already.
// The order here is the canonical sidebar order (enforced by `setOrder`).
//
// `system-health` is `hidden: true` at the stub level: the spec says it
// should only appear in the sidebar when the real System Health page module
// is registered. Real pages can register themselves with `hidden: false`
// (their default) to surface in the nav.

interface StubSpec {
  name: string;
  label: string;
  icon: string;
  hidden?: boolean;
}

const STUBS: StubSpec[] = [
  { name: "dashboard",             label: "Dashboard",     icon: "🏠" },
  { name: "demo",                  label: "Demo",          icon: "🎬" },
  { name: "retailers",             label: "Retailers",     icon: "🏪" },
  { name: "prospects",             label: "Prospects",     icon: "🎯" },
  { name: "suppliers",             label: "Suppliers",     icon: "🏭" },
  { name: "catalogue-pdf-import",  label: "PDF Import",    icon: "📄" },
  { name: "catalogue-supplier-import", label: "Supplier Import", icon: "📥" },
  { name: "supplier-discounts",    label: "Discounts",     icon: "💷" },
  { name: "door-ranges",           label: "Door Ranges",   icon: "🚪" },
  { name: "catalogue-access",      label: "Catalogue Access", icon: "🔐" },
  { name: "communication-monitor", label: "Comm Monitor",  icon: "🛡️" },
  { name: "phone-numbers",         label: "Phone Numbers", icon: "📞" },
  { name: "onboarding",            label: "Onboarding",    icon: "🚀" },
  { name: "analytics",     label: "Analytics",     icon: "📊" },
  { name: "usage",         label: "Portal Usage",  icon: "📈" },
  { name: "email",         label: "Email",         icon: "✉️" },
  { name: "social",        label: "Social",        icon: "📱" },
  { name: "tasks",         label: "Tasks",         icon: "✅" },
  { name: "notes",         label: "Notes",         icon: "📝" },
  { name: "events",        label: "Events",        icon: "📅" },
  { name: "links",         label: "Links",         icon: "🔗" },
  { name: "settings",      label: "Settings",      icon: "⚙️" },
  { name: "feedback",      label: "Feedback",      icon: "💬" },
  { name: "gdpr",          label: "GDPR",          icon: "⚖️" },
  { name: "roadmap",       label: "Roadmap",       icon: "🗺️" },
  { name: "system-health", label: "System Health", icon: "❤️", hidden: true },
  { name: "flagged-layouts", label: "Flagged Layouts", icon: "🚩" },
  { name: "app-launcher",  label: "App Launcher",  icon: "📐" },
];

function makeStub(spec: StubSpec): Page {
  const base: Page = {
    name: spec.name,
    label: spec.label,
    icon: spec.icon,
    mount(container: HTMLElement, _ctx: PageContext): PageCleanup {
      container.innerHTML = `
        <section class="adm-placeholder">
          <h1>${spec.label}</h1>
          <p>Coming soon — extracting from source-of-truth.</p>
        </section>
      `;
      return () => {
        /* no-op cleanup */
      };
    },
  };
  if (spec.hidden) {
    return { ...base, hidden: true };
  }
  return base;
}

// ── Register stubs only when the real page hasn't claimed the name ──────────
for (const spec of STUBS) {
  if (getPage(spec.name)) continue;
  registerPage(makeStub(spec));
}

// ── Enforce canonical sidebar order ─────────────────────────────────────────
// Real pages registered first via imports would otherwise appear at the top
// of the sidebar — this re-orders to the spec's canonical layout.
setOrder(STUBS.map((s) => s.name));
